import { availableCustomAttributesByType } from '.';

import type { ReduxRootState } from 'types';

type ReturnedFilters = {
  atStart: { [title: string]: Record<'label' | 'value', string>[] };
  atEnd: { [title: string]: Record<'label' | 'value', string>[] };
};

interface Params
  extends Pick<ReduxRootState, 'language' | 'messages' | 'serverConfig'> {
  type:
    | 'achievement'
    | 'calendarEvent'
    | 'course'
    | 'individualAchievement'
    | 'path';
  filterCustomAttributes?: {
    key: string;
    values: string[];
  }[];
}

const augmentFiltersWithCustomAttributes = ({
  filterCustomAttributes,
  language,
  messages,
  serverConfig,
  type
}: Params): ReturnedFilters => {
  if (!type) return { atStart: {}, atEnd: {} };

  const availableAttributes = availableCustomAttributesByType({
    serverConfig,
    type
  });
  if (!availableAttributes?.length) return { atStart: {}, atEnd: {} };

  let atStart = {} as ReturnedFilters['atStart'];
  let atEnd = {} as ReturnedFilters['atEnd'];
  availableAttributes.forEach((attr) => {
    if (!attr.displayInProductFilters) return;

    // check if filterCustomAttributes is provided
    let customAttributeFilter: { key: string; values: string[] } | undefined;
    if (serverConfig?.useConditionalHidingOfFilterOptions) {
      const customAttributeFilter = filterCustomAttributes?.find(
        (filter) => filter.key === attr.key
      );
      if (filterCustomAttributes && !customAttributeFilter) return;
    }

    const options = new Set<string | boolean>();

    // if filterCustomAttributes is provided and conditional hiding is in use,
    // only include options from its values
    // NOTE: missing values for boolean attributes are treated as false, but
    // this does not currently work with conditional hiding
    if (customAttributeFilter) {
      customAttributeFilter.values.forEach((value) => options.add(value));
    } else if (
      attr.type === 'ArrayWithOptions' ||
      attr.type === 'StringWithOptions'
    ) {
      // otherwise, include all available options
      attr.options.forEach((option) =>
        option.options.forEach((value) => options.add(value))
      );
    } else if (attr.type === 'Boolean') {
      options.add(true);
      options.add(false);
    }

    // if no options, skip this attribute
    if (!options.size) return;

    const filterGroup = {
      [attr.titleData?.[language || serverConfig?.defaultSystemLanguage] ||
      attr.title]: Array.from(options).map((value) => ({
        label:
          typeof value === 'boolean'
            ? value
              ? messages.commonYes
              : messages.commonNo
            : value,
        value: `&customAttribute.${attr.key}=${encodeURIComponent(value)}`
      }))
    };

    if (attr.displayInProductFiltersAtStart) {
      atStart = { ...atStart, ...filterGroup };
    } else {
      atEnd = { ...atEnd, ...filterGroup };
    }
  });

  return { atStart, atEnd };
};

export default augmentFiltersWithCustomAttributes;
